<script setup lang="ts">
import type { MaskInputOptions } from "maska";

import { watchDebounced } from "@vueuse/core";
import { vMaska } from "maska/vue";
import { computed, nextTick, onMounted, reactive, ref, useCssModule, watch } from "vue";

import Modal from "@/components/modal/modal.vue";
import StepsButtons from "@/components/StepsButtons/StepsButtons.vue";
import { BugTrackerSection, useBugTracker } from "@/helpers/bugTracker";
import parseStringPrice from "@/helpers/parseStringPrice";
import WIDGET_ANALYTICS_EVENTS from "@/helpers/widgetAnalyticsEvents/widgetAnalyticsEvents";
import {
    apiGetParcelMachines,
    type ParcelMachine,
} from "@/hooks/requests/apiGetParcelMachines/apiGetParcelMachines";
import {
    apiPostDeliveryAddress,
    ApiPostDeliveryAddressPayload,
} from "@/hooks/requests/apiPostDeliveryAddress/apiPostDeliveryAddress";
import createApplication from "@/hooks/requests/createApplication/createApplication";
import { useAnalytics } from "@/hooks/useAnalytics/useAnalytics";
import useTranslate from "@/hooks/useTranslate/useTranslate";
import useValidate from "@/hooks/useValidate";
import { useChooseDeviceStore } from "@/stores/chooseDevice/chooseDevice";
import { useDeliveryStore } from "@/stores/delivery/delivery";
import { useHowYouRateDeviceStore } from "@/stores/howYouRateDevice/howYouRateDevice";
import { useSuccessfulEvaluationStore } from "@/stores/successfulEvaluation/successfulEvaluation";
import { useTIWStore } from "@/stores/tiw/tiw";
import Checkbox from "@/ui/checkbox/checkbox.vue";
import Field from "@/ui/field/field.vue";
import Input from "@/ui/input/input.vue";
import Select from "@/ui/select/select.vue";

import { getDeliveryDays } from "./thanksYou";

const classes = useCssModule();

onMounted(() => {
    useAnalytics(WIDGET_ANALYTICS_EVENTS.PICKUP_ADDRESS);
});

const TIWStore = useTIWStore();
const DeliveryStore = useDeliveryStore();
const SuccessfulEvaluationStore = useSuccessfulEvaluationStore();
const HowYouRateDeviceStore = useHowYouRateDeviceStore();
const ChooseDeviceStore = useChooseDeviceStore();

const isSameAddress = ref(true);
const loadingToNext = ref(false);

type DeliveryTypes = ApiPostDeliveryAddressPayload["deliveryType"];
const selectedDeliveryType = ref<DeliveryTypes>("courier");
async function selectDeliveryType(type: DeliveryTypes) {
    if (!document.startViewTransition) {
        selectedDeliveryType.value = type;
        return;
    }

    const transition = document.startViewTransition(async () => {
        selectedDeliveryType.value = type;
        await nextTick();
    });

    await transition.finished;
}
const parcelMachines = ref<ParcelMachine[]>([]);
const unwatch = watch(selectedDeliveryType, async (deliveryType) => {
    if (deliveryType === "locker") {
        parcelMachines.value = await getParcelMachines({ search: parcelMachineInputValue.value });
        unwatch();
    }
});
const isParcelMachinesLoading = ref(false);
async function getParcelMachines(params: { search: string }): Promise<ParcelMachine[]> {
    let result: ParcelMachine[] = [];

    try {
        const parcelMachinesResponse = await apiGetParcelMachines(params);
        result = parcelMachinesResponse.data;
    } catch (e) {
        console.error("error when posts adresses loading: ", e);
        useBugTracker("getting all posts", BugTrackerSection.ERROR, {
            message: e.message,
        });
    }

    return result;
}
const selectedParcelMachineName = ref("");
const selectedParcelMachineAddress = ref("");
const parcelMachineInputValue = ref("");
const MIN_SEARCH_LENGTH = 3;
const noParcelMachinesText = ref("Proszę wpisać co najmniej 3 znaki"); // FIXME перевод
watchDebounced(
    parcelMachineInputValue,
    async (inputValue) => {
        const search = inputValue.trim().toLowerCase();

        if (search.length < MIN_SEARCH_LENGTH) {
            parcelMachines.value = [];
            noParcelMachinesText.value = "Proszę wpisać co najmniej 3 znaki"; // FIXME перевод

            return;
        }

        isParcelMachinesLoading.value = true;
        noParcelMachinesText.value = "Proszę czekać..."; // FIXME перевод

        parcelMachines.value = await getParcelMachines({ search });

        isParcelMachinesLoading.value = false;
        noParcelMachinesText.value = "";
    },
    {
        debounce: 500,
    },
);

const canNextStep = computed(() => {
    if (selectedDeliveryType.value === "locker") {
        return Boolean(selectedParcelMachineName.value);
    } else {
        return DeliveryStore.isCanNext && !loadingToNext.value;
    }
});

const handedDevicePrice = computed(() => {
    if (typeof HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale === "object") {
        return HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale?.tradeInSale;
    }

    return HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale;
});

function setArrivalDate(date: string) {
    DeliveryStore.deliveryInfo.arrivalDate = date;
}

async function onNext() {
    loadingToNext.value = true;

    const field =
        SuccessfulEvaluationStore.formType === "individual" ? "individualFields" : "companyFields";
    try {
        await createApplication({
            device_id: HowYouRateDeviceStore.registeringDeviceData.deviceId,
            ex_id: HowYouRateDeviceStore.registeringDeviceData.deviceId,
            imei: ChooseDeviceStore.field_imei.value,
            locale: TIWStore.standardizedLocale,
            location: TIWStore.WIDGET_DATA.location,
            source: TIWStore.WIDGET_DATA.code,
            phone: SuccessfulEvaluationStore[field].phone.replace(/\D+/g, ""),
            city_name: SuccessfulEvaluationStore[field].city,
            email: SuccessfulEvaluationStore[field].email,
            first_name: SuccessfulEvaluationStore[field].name,
            last_name: SuccessfulEvaluationStore[field].surname,
            company_name: SuccessfulEvaluationStore.companyFields.companyName,
            pickup_date: DeliveryStore.deliveryInfo.arrivalDate,
            contact_person: {
                address:
                    `${DeliveryStore.deliveryInfo.street} ${DeliveryStore.deliveryInfo.houseNumber}, apt. ${DeliveryStore.deliveryInfo.apartmentNumber}`.trim(),
                city_name: DeliveryStore.deliveryInfo.city,
                first_name: DeliveryStore.deliveryInfo.name,
                last_name: DeliveryStore.deliveryInfo.surname,
                phone: DeliveryStore.deliveryInfo.phone.replace(/\D+/g, ""),
            },
            grade: HowYouRateDeviceStore.registeringDeviceData.grade,
            brand: ChooseDeviceStore.brandName,
            model: ChooseDeviceStore.field_model.value.name,
            price: parseStringPrice(handedDevicePrice.value),
            address:
                selectedDeliveryType.value === "locker"
                    ? selectedParcelMachineName.value
                    : SuccessfulEvaluationStore.address,
        });

        const DELIVERY_SERVICE: ApiPostDeliveryAddressPayload["deliveryService"] = "in_post";
        await apiPostDeliveryAddress({
            token: TIWStore.WIDGET_DATA.token,
            deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId,
            contactFirstName: DeliveryStore.deliveryInfo.name,
            contactLastName: DeliveryStore.deliveryInfo.surname,
            contactPhone: DeliveryStore.deliveryInfo.phone.replace(/\D+/g, ""),
            contactEmail: SuccessfulEvaluationStore.individualFields.email,
            index: DeliveryStore.deliveryInfo.postalCode,
            city: DeliveryStore.deliveryInfo.city,
            street: DeliveryStore.deliveryInfo.street,
            houseNum: DeliveryStore.deliveryInfo.houseNumber,
            apartmentNum: DeliveryStore.deliveryInfo.apartmentNumber,
            deliveryService: DELIVERY_SERVICE,
            deliveryType: selectedDeliveryType.value,
            customField1:
                selectedDeliveryType.value === "locker" ? selectedParcelMachineName.value : null,
        });
    } catch (e) {
        useBugTracker(
            "Successful Evaluation --- [POST] Create Application",
            BugTrackerSection.ERROR,
            {
                message: e?.message,
            },
        );
        loadingToNext.value = false;
        return;
    }

    TIWStore.switchStep("thanksForOrder");
}

const phoneMaskOptions = reactive<MaskInputOptions>({
    mask: "+48 (___) ___-___",
    tokens: {
        _: {
            pattern: /\d/,
        },
        A: {
            pattern: /[1-9]/,
        },
    },
    eager: true,
});

const arrivalDates = getDeliveryDays().map((el) => {
    return { date: el.date, dateLabel: el.label, timeStart: "08:00", timeEnd: "18:00" };
});

watch(
    isSameAddress,
    () => {
        if (isSameAddress.value) {
            const fieldType =
                SuccessfulEvaluationStore.formType === "individual"
                    ? "individualFields"
                    : "companyFields";
            DeliveryStore.deliveryInfo.name = SuccessfulEvaluationStore[fieldType].name;
            DeliveryStore.deliveryInfo.apartmentNumber =
                SuccessfulEvaluationStore[fieldType].apartmentNumber;
            DeliveryStore.deliveryInfo.city = SuccessfulEvaluationStore[fieldType].city;
            DeliveryStore.deliveryInfo.houseNumber =
                SuccessfulEvaluationStore[fieldType].houseNumber;
            DeliveryStore.deliveryInfo.phone = SuccessfulEvaluationStore[fieldType].phone;
            DeliveryStore.deliveryInfo.street = SuccessfulEvaluationStore[fieldType].street;
            DeliveryStore.deliveryInfo.surname = SuccessfulEvaluationStore[fieldType].surname;
            DeliveryStore.deliveryInfo.postalCode = SuccessfulEvaluationStore[fieldType].postalCode;
        } else {
            DeliveryStore.deliveryInfo.name = "";
            DeliveryStore.deliveryInfo.apartmentNumber = "";
            DeliveryStore.deliveryInfo.city = "";
            DeliveryStore.deliveryInfo.houseNumber = "";
            DeliveryStore.deliveryInfo.phone = "";
            DeliveryStore.deliveryInfo.street = "";
            DeliveryStore.deliveryInfo.postalCode = "";
            DeliveryStore.deliveryInfo.surname = "";
        }
    },
    { immediate: true },
);

const phoneValidation = computed(() => {
    if (!DeliveryStore.deliveryInfo.phone) {
        return {
            status: true,
            message: "ok",
        };
    }
    const valiDate = useValidate({
        target: "phone",
        value: DeliveryStore.deliveryInfo.phone,
    });
    return valiDate;
});
</script>

<template>
    <Modal class="tiw__step__thanksYou">
        <template #header>
            <div class="tiw__step__thanksYou__header">
                <img src="\img\success.svg" alt="" />
                <p class="tiw__h2 tiw__step__thanksYou__header__title">
                    {{ useTranslate("tiw__thanksYou__title") }}
                </p>
                <p class="tiw__step__thanksYou__header__desc">
                    {{ useTranslate("tiw__thanksYou__text") }}
                </p>
                <p class="tiw__step__thanksYou__header__desc">
                    <!-- FIXME перевод -->
                    Wybierz, jak chcesz przekazać urządzenie: kurierem lub przez paczkomat.
                </p>
            </div>
        </template>

        <div class="tiw__step__thanksYou__body" :class="classes.inputsContainer">
            <div :class="classes.deliveryTypeSwitcher">
                <span
                    :class="[
                        classes.switchBtn,
                        { [classes.selected]: selectedDeliveryType === 'courier' },
                    ]"
                    @click="selectDeliveryType('courier')"
                    >Kurier</span
                >
                <span
                    :class="[
                        classes.switchBtn,
                        { [classes.selected]: selectedDeliveryType === 'locker' },
                    ]"
                    @click="selectDeliveryType('locker')"
                    >Paczkomat</span
                >
            </div>

            <template v-if="selectedDeliveryType === 'courier'">
                <b
                    ><p>{{ useTranslate("tiw__thanksYou__pleaseProvide") }}</p></b
                >
                <Checkbox
                    v-model="isSameAddress"
                    class="tiw__step__thanksYou__sameAddress"
                    :checked="isSameAddress"
                >
                    {{ useTranslate("tiw__thanksYou_useTheSameAddress") }}
                </Checkbox>
                <form>
                    <Field>
                        <template #hintTop>
                            <span>
                                {{ useTranslate("tiw__forms__name__recipient") }}
                            </span>
                        </template>
                        <template #body>
                            <Input
                                v-model="DeliveryStore.deliveryInfo.name"
                                type="text"
                                name="recipientName"
                            />
                        </template>
                        <template #hintBottom>
                            <span>
                                <!-- {{ docCityNameValidation.message }} -->
                            </span>
                        </template>
                    </Field>
                    <Field>
                        <template #hintTop>
                            <span>
                                {{ useTranslate("tiw__forms__surname__recipient") }}
                            </span>
                        </template>
                        <template #body>
                            <Input
                                v-model="DeliveryStore.deliveryInfo.surname"
                                type="text"
                                name="recipientSurname"
                            />
                        </template>
                        <template #hintBottom>
                            <span>
                                <!-- {{ docCityNameValidation.message }} -->
                            </span>
                        </template>
                    </Field>
                    <Field>
                        <template #hintTop>
                            <span>
                                {{ useTranslate("tiw__forms__postalCode") }}
                            </span>
                        </template>
                        <template #body>
                            <Input
                                v-model="DeliveryStore.deliveryInfo.postalCode"
                                type="text"
                                name="postalCode"
                            />
                        </template>
                        <template #hintBottom>
                            <span>
                                <!-- {{ docCityNameValidation.message }} -->
                            </span>
                        </template>
                    </Field>
                    <Field>
                        <template #hintTop>
                            <span>
                                {{ useTranslate("tiw__forms__city") }}
                            </span>
                        </template>
                        <template #body>
                            <Input
                                v-model="DeliveryStore.deliveryInfo.city"
                                type="text"
                                name="city"
                            />
                        </template>
                        <template #hintBottom>
                            <span>
                                <!-- {{ docCityNameValidation.message }} -->
                            </span>
                        </template>
                    </Field>
                    <Field>
                        <template #hintTop>
                            <span>
                                {{ useTranslate("tiw__forms__street") }}
                            </span>
                        </template>
                        <template #body>
                            <Input
                                v-model="DeliveryStore.deliveryInfo.street"
                                type="text"
                                name="street"
                            />
                        </template>
                        <template #hintBottom>
                            <span>
                                <!-- {{ docCityNameValidation.message }} -->
                            </span>
                        </template>
                    </Field>
                    <div class="tiw__step__thanksYou__body__houseApartment">
                        <Field>
                            <template #hintTop>
                                <span>
                                    {{ useTranslate("tiw__forms__houseNumber") }}
                                </span>
                            </template>
                            <template #body>
                                <Input
                                    v-model="DeliveryStore.deliveryInfo.houseNumber"
                                    type="text"
                                    name="houseNumber"
                                />
                            </template>
                            <template #hintBottom>
                                <span>
                                    <!-- {{ docCityNameValidation.message }} -->
                                </span>
                            </template>
                        </Field>
                        <Field>
                            <template #hintTop>
                                <span>
                                    {{ useTranslate("tiw__forms__apartmentNumber") }}
                                </span>
                            </template>
                            <template #body>
                                <Input
                                    v-model="DeliveryStore.deliveryInfo.apartmentNumber"
                                    type="text"
                                    name="apartmentNumber"
                                />
                            </template>
                            <template #hintBottom>
                                <span>
                                    <!-- {{ docCityNameValidation.message }} -->
                                </span>
                            </template>
                        </Field>
                    </div>
                    <Field :is-error="!phoneValidation.status">
                        <template #hintTop>
                            <span>
                                {{ useTranslate("tiw__forms__phone") }}
                            </span>
                        </template>
                        <template #body>
                            <Input
                                v-model="DeliveryStore.deliveryInfo.phone"
                                v-maska="phoneMaskOptions"
                                type="text"
                                name="recipientPhone"
                                :placeholder="phoneMaskOptions.mask"
                            />
                        </template>
                        <template v-if="!phoneValidation.status" #hintBottom>
                            <span>
                                {{ phoneValidation.message }}
                            </span>
                        </template>
                    </Field>
                </form>
                <p class="tiw__step__thanksYou__arrivalDate__title">
                    <b>{{ useTranslate("tiw__thanksYou_pleaseSpecifyArrivalDate") }}</b>
                </p>
                <div class="tiw__step__thanksYou__body__arrivalDate__items">
                    <div
                        v-for="arrivalDate in arrivalDates"
                        :key="arrivalDate.date"
                        :class="
                            arrivalDate.date === DeliveryStore.deliveryInfo.arrivalDate
                                ? 'tiw__step__thanksYou__body__arrivalDate__item__active'
                                : ''
                        "
                        class="tiw__step__thanksYou__body__arrivalDate__item"
                        @click="setArrivalDate(arrivalDate.date)"
                    >
                        <p class="tiw__step__thanksYou__body__arrivalDate__item__title">
                            {{ arrivalDate.dateLabel }}
                        </p>
                        <p class="tiw__step__thanksYou__body__arrivalDate__item__date">
                            {{ arrivalDate.timeStart }} - {{ arrivalDate.timeEnd }}
                        </p>
                    </div>
                </div>
            </template>
            <template v-else>
                <b>
                    <!-- TODO перевод -->
                    <p :class="classes.postHeader">Podaj adres paczkomatu</p>
                </b>
                <Select
                    v-model="selectedParcelMachineName"
                    :reduce="
                        (parcelMachine: ParcelMachine) => {
                            selectedParcelMachineAddress = parcelMachine.name_pl;
                            return parcelMachine.ref;
                        }
                    "
                    :options="parcelMachines"
                    @search="(query: string) => (parcelMachineInputValue = query)"
                    :filterable="false"
                    name="selectParcelMachine"
                    label="ref"
                    :is-loading="isParcelMachinesLoading"
                >
                    <template #option="{ ref, name_pl }">
                        <div>
                            <b>{{ `Inpost Paczkomat ${ref}` }}</b>
                            <p>{{ name_pl }}</p>
                        </div>
                    </template>

                    <template #selected-option="{ ref }">
                        <p>{{ `Inpost Paczkomat ${ref}` }}</p>
                    </template>

                    <template #no-options v-if="noParcelMachinesText">
                        {{ noParcelMachinesText }}
                    </template>
                </Select>

                <div v-if="selectedParcelMachineName" :class="classes.selectedParcelMachineCard">
                    <figure>
                        <img src="@/public/svg/post-adress.svg" alt="post icon" width="45px" />
                    </figure>

                    <div>
                        <b>Inpost Paczkomat {{ selectedParcelMachineName }}</b>
                        <p>{{ selectedParcelMachineAddress }}</p>
                    </div>
                </div>
            </template>

            <!-- FIXME перевод -->
            <StepsButtons
                :click-to-next="onNext"
                :disabled="!canNextStep"
                :loading="loadingToNext"
                :no-back-btn="true"
                :btn-text="
                    selectedDeliveryType === 'courier' ? 'Zamów kuriera' : 'Wybierz paczkomat'
                "
            />
        </div>
    </Modal>
</template>

<style lang="scss" src="./thanksYou.scss"></style>
<style module>
.inputsContainer {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.deliveryTypeSwitcher {
    display: flex;
    justify-content: center;
    gap: 48px;
    border-bottom: 1px solid #e5e5e5;

    .switchBtn {
        cursor: pointer;
        padding-bottom: 10px;
        &.selected {
            /* FIXME use variable */
            border-bottom: 4px solid var(--main-green);
        }
    }
}

.postHeader {
    margin-top: 40px;
    margin-bottom: 16px;
}

.selectedParcelMachineCard {
    display: flex;
    gap: 15px;
    padding: 20px;
    background-color: #f5f5f5;
    border-radius: 8px;

    figure {
        margin: 0;
    }
}
</style>
